import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
	View,
	Text,
	StyleSheet,
	Dimensions,
	TouchableOpacity,
	ScrollView,
	Pressable,
} from 'react-native';
import SolidButtonContainer from '../../../../components/shared/Buttons/SolidButton.container';
import FieldBuilder from '../../../../components/shared/FieldBuilder/FieldBuilder';
import I18n from '../../../../components/shared/i18n/I18n.component';
import { geocodeByPlaceId } from 'react-google-places-autocomplete';
import colors from '../../../../config/colors';
import getShadowStyle from '../../../../config/shadow';
import httpService from '../../../../services/http.service';
import {
	DepFilterData,
	EmployeeSelector,
	ProjectSelector,
	UserData,
} from './LocationSettingsModal.utils';
import { Privileges } from '../../../../constants/roleTypes.constant';
import { hasPermissionOnBusinessUnit } from '../../../../utils/globals.utils';
import ButtonV2 from '../../../../components/shared/Buttons/ButtonV2.container';

interface Props {
	users: DepFilterData[];
	locationId?: string;
	projectsFilterList: any[];
	departmentsFilterList: any[];
	locationData?: any;
	onNext: Function;
	refresh(): void;
	unitIds?: number[];
	businessUnitsList: any[];
	businessUnitId?: number;
	onClose(): Function;
}

const windowHeight = Dimensions.get('window').height;
const windowWidth = Dimensions.get('window').width;

const LocationSettingsModal = ({
	users = [],
	locationId,
	locationData,
	projectsFilterList,
	refresh,
	onNext,
	unitIds,
	businessUnitsList,
	businessUnitId,
	onClose,
}: Props) => {
	const [activeIndex, setActiveIndex] = useState(0);
	const [userList, setUserList] = useState(users);
	const [projectList, setProjectList] = useState(projectsFilterList);

	const [usersData, setUsersData] = useState<UserData>({
		users: [],
	});
	const [projectsData, setProjectsData] = useState<UserData>({
		projects: [],
		users: [],
	});

	const [selectedLocation, setSelectedLocation] = useState<any>(null);
	const [selectedUnitdId, setSelectedUnitdId] = useState<any>(null);
	const [name, setName] = useState('');
	const [selectedRadius, setSelectedRadius] = useState<any>(500);
	const [disableSave, setDisableSave] = useState(true);
	const [refreshUsersByProjects, setRefreshUsersByProjects] = useState(false);
	const [showBottomSection, setShowBottomSection] = useState(
		!!locationId || unitIds?.length === 1,
	);
	const [{ radiusError, nameError }, setError] = useState({
		radiusError: false,
		nameError: false,
	});

	const gerProjectsList = async (unitId: any) => {
		try {
			const res = await httpService.api<any>({
				type: 'getUnitProjectsFilter',
				query: {
					pageName: 'locationSettings',
					unitIds: unitId,
				},
			});

			if (res) {
				setProjectList(res);
			}
		} catch (e) {}
	};

	const getUserList = async (unitId: number) => {
		const res = await httpService.api<any>({
			type: 'getUsersByDepartments',
			query: {
				pageName: 'locationSettings',
				unitId,
			},
			data: {
				search: '',
				f: { dep: [], internal: ['true'] },
			},
		});
		setUserList(res);
	};

	useEffect(() => {
		if (locationId) {
			setProjectsData(() => ({
				projects: locationData?.projectsData?.projects || [],
				users: locationData?.projectsData?.users || [],
			}));
			setUsersData(() => ({
				users: locationData?.usersData || [],
			}));
			setSelectedLocation(locationData?.location);
			setSelectedRadius(locationData?.radius);
			setName(locationData?.name);
		}

		setUserList(() => [...users]);
	}, [locationData]);

	useEffect(() => {
		let disable = true;
		if (!!usersData?.users?.length) {
			if (
				!!projectsData.users.length === !!projectsData?.projects?.length
			) {
				disable = false;
			}
		}
		if (!!projectsData?.projects?.length && !!projectsData.users.length) {
			disable = false;
		}

		setDisableSave(!selectedLocation || disable);
	}, [usersData, projectsData, selectedLocation]);

	const arrangeData = async () => {
		try {
			const data: any = formMethods.getValues();
			const latLon = await geocodeByPlaceId(selectedLocation?.placeId);

			let newData: any = {
				location: {
					...selectedLocation,
					lat: latLon[0].geometry.location.lat(),
					lon: latLon[0].geometry.location.lng(),
				},
				name: data?.name ? data?.name : null,
				radius: data.radius,
				status: data.status,
				usersData: usersData.users,
				unitId: data.unitId || businessUnitId,
				projectsData: {
					...projectsData,
				},
			};
			if (!!locationId) {
				newData = { ...newData, id: locationId };
			}
			return newData;
		} catch (e) {}
	};

	const validate = () => {
		let radErr = false;
		let nameErr = false;
		if (selectedRadius > 5000 || selectedRadius < 200) {
			radErr = true;
		}
		if (!!name && (name.length < 2 || name.length > 30)) {
			nameErr = true;
		}
		setError(prev => ({
			...prev,
			radiusError: radErr,
			nameError: nameErr,
		}));
		return radErr || nameErr;
	};

	const createNewLocation = async onNext => {
		try {
			const isError = validate();
			if (!isError) {
				const data = await arrangeData();

				const res = await httpService.api<any>({
					type: 'createNewLocation',
					data,
				});
				onNext(refresh());
			}
		} catch (e) {}
	};

	const updateLocation = async onNext => {
		try {
			const isError = validate();
			if (!isError) {
				const data = await arrangeData();

				const res = await httpService.api<any>({
					type: 'updateLocation',
					params: { locationId },
					data,
				});
				onNext(refresh());
			}
		} catch (e) {}
	};

	const setLocationData = val => {
		const newLocation = {
			locationName: val.label,
			placeId: val?.value?.place_id,
		};

		setSelectedLocation(prev => ({ ...prev, ...newLocation }));
	};

	const onRadiusChange = (rad: number = 0) => {
		setSelectedRadius(rad);
	};

	const formMethods = useForm({
		defaultValues: {
			radius: 500,
			status: true,
			address: locationData?.location?.locationName || '',
			unitId:
				locationId || unitIds?.length !== 1 ? undefined : unitIds[0],
			...locationData,
		},
		mode: 'onBlur',
	});

	const canEdit = useMemo(() => {
		const data = formMethods?.getValues();
		return (
			(data?.unitId || businessUnitId) &&
			hasPermissionOnBusinessUnit(
				data?.unitId || businessUnitId,
				Privileges.ROLE_LOCATION_WRITE,
			)
		);
	}, [businessUnitId, selectedUnitdId]);

	const canEditAssociation = useMemo(() => {
		const data = formMethods.getValues();
		return (
			(data.unitId || businessUnitId) &&
			hasPermissionOnBusinessUnit(
				data.unitId || businessUnitId,
				Privileges.ROLE_LOCATION_ASSOCIATION_WRITE,
			)
		);
	}, [businessUnitId, selectedUnitdId]);

	useEffect(() => {
		const subscription = formMethods.watch((value, { name, type }) => {
			console.log({ type, name });
			if (type === 'change' && name === 'unitId' && value[name]) {
				setShowBottomSection(true);
				setUsersData({ users: [] });
				setProjectsData({
					projects: [],
					users: [],
				});
				getUserList(value[name]);
				setSelectedUnitdId(value[name]);
				gerProjectsList(value[name]);
			}
		});
		return () => subscription.unsubscribe();
	}, [formMethods.watch]);

	const updateUsers = (newUserData: UserData) => {
		setUsersData((prev: UserData) => ({ ...prev, ...newUserData }));
	};

	const updateProjects = (newUserData: UserData) => {
		if (
			newUserData.projects?.length === 0 &&
			projectsData.projects?.length !== 0
		) {
			newUserData.users = [];
			setRefreshUsersByProjects(prev => !prev);
		}
		setProjectsData((prev: UserData) => ({ ...prev, ...newUserData }));
	};

	return (
		<FormProvider {...formMethods}>
			<View style={[styles.container, { height: windowHeight - 100 }]}>
				<View style={{ flexDirection: 'row-reverse', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
					
					<View style={{ flexDirection: 'row-reverse', gap: 16, alignItems: 'center' }}>
						<I18n
							size={20}
							weight='bold'
							color='primaryDarkBlue'
						>
							{locationId
								? 'locationSettings.modalTitle.editLocation'
								: 'locationSettings.modalTitle.newLocation'}
						</I18n>
						<View style={{ }}>
							<FieldBuilder
								name={'status'}
								type={'inlineCheckbox'}
								label={'general.active'}
								disabled={!canEdit}
								styles= {{
									wrapper: {
										margin: 0
									},
									labelWrapper: {
										marginBottom: 0,
									}
								}}
							/>
						</View>
					</View>

					<Pressable onPress={onClose} style={styles.closeButton}>
						<Text style={styles.closeButtonText}>×</Text>
					</Pressable>

				</View>
				
				<ScrollView
					contentContainerStyle={{
						minHeight: 550,
						paddingHorizontal: 5,
					}}
				>
					<View style={[styles.sectionContainer, { zIndex: 100 }]}>

						<I18n
							size={17}
							weight='bold'
							color='primaryDarkBlue'
							style={{ marginBottom: 24 }}
						>
							locationSettings.modal.general
						</I18n>

						<View style={{ flexDirection: 'row-reverse' }}>
							<View style={[styles.formRow, { zIndex: 100 }]}>
								{!locationId &&
									businessUnitsList.length > 1 && (
										<FieldBuilder
											name='unitId'
											type='reactselect'
											label='general.unitIds'
											showFullName
											isRequired
											options={businessUnitsList}
											styles={{
												control: {
													height: 40,
													minHeight: 40,
													borderRadius: 8,
													color: colors.clockRecordBlue,
													borderColor: colors.clockGreyText,
													backgroundColor: 'white',
												},
												placeholder: { color: colors.clockRecordBlue },
											}}
											labelStyle={{ color: colors.clockRecordBlue}}
										/>
									)}
							</View>
							<View style={[styles.formRow, { zIndex: 100 }]}>
								<FieldBuilder
									name='address'
									type='googlePlacesAutocomplete'
									label='locationSettings.table.address'
									showFullName
									staticWidth={250}
									isRequired
									maxMenuHeight={150}
									getAllData={setLocationData}
									disabled={!canEdit}
									selectControlStyles={{
										height: 40,
										borderRadius: 8,
										color: colors.clockRecordBlue,
										borderColor: colors.clockGreyText,
										backgroundColor: 'white',
										borderWidth: 1,
										borderBottomWidth: 1,
									}}
									labelStyle={{ color: colors.clockRecordBlue }}
								/>
							</View>
							<View style={styles.formRow}>
								<FieldBuilder
									name='name'
									type='input'
									label='locationSettings.table.addressName'
									onChange={(a, b, c) => setName(c)}
									error={
										nameError
											? {
													message:
														'locationSettings.modal.nameError',
											  }
											: null
									}
									disabled={!canEdit}
									style={{
										height: 40,
										borderRadius: 8,
										color: colors.clockRecordBlue,
										borderColor: colors.clockGreyText,
										borderWidth: 1,
										borderBottomWidth: 1,
										backgroundColor: 'white',
									}}
									labelStyle={{ color: colors.clockRecordBlue }}
								/>
							</View>
							<View style={styles.formRow}>
								<FieldBuilder
									name='radius'
									type='input'
									keyboardType='numeric'
									label='locationSettings.table.radius'
									isRequired
									maxVal={5000}
									onChange={(a, b, c) => onRadiusChange(c)}
									error={
										radiusError
											? {
													message:
														'locationSettings.modal.radiusError',
											  }
											: null
									}
									disabled={!canEdit}
									style={{
										height: 40,
										borderRadius: 8,
										color: colors.clockRecordBlue,
										borderColor: colors.clockGreyText,
										backgroundColor: 'white',
										borderWidth: 1,
										borderBottomWidth: 1,
									}}
									labelStyle={{ color: colors.clockRecordBlue }}
								/>
							</View>
						</View>
					</View>
					{showBottomSection && (
						<>
							<View style={styles.tabsContainer}>
								<TouchableOpacity
									onPress={() => setActiveIndex(0)}
									style={[
										styles.buttonTab,
										{
											borderBottomColor:
												activeIndex === 0
													? colors.primaryDarkBlue
													: colors.white,
										},
									]}
								>
									<I18n size={14} color='primaryDarkBlue' weight='bold'>
										locationSettings.modal.employee
									</I18n>
								</TouchableOpacity>

								{projectList.length ? (
									<TouchableOpacity
										onPress={() => setActiveIndex(1)}
										style={[
											styles.buttonTab,
											{
												borderBottomColor:
													activeIndex === 1
														? colors.primaryDarkBlue
														: colors.white,
											},
										]}
									>
										<I18n size={14} color='primaryDarkBlue' weight='bold'>
											locationSettings.modal.projects
										</I18n>
									</TouchableOpacity>
								) : (
									<View />
								)}
							</View>
							{userList.length ? (
								<View
									style={[
										styles.tabContainer,
										{
											minHeight: 400,
											height: windowHeight - 540,
										},
									]}
								>
									{activeIndex === 0 ? (
										<EmployeeSelector
											userList={userList}
											userData={usersData}
											setUserData={updateUsers}
											disabled={
												!canEdit && !canEditAssociation
											}
										/>
									) : projectList.length ? (
										<ProjectSelector
											userList={userList}
											projects={projectList}
											userData={projectsData}
											setUserData={updateProjects}
											refreshUsersByProjects={
												refreshUsersByProjects
											}
											disabled={
												!canEdit && !canEditAssociation
											}
										/>
									) : (
										<View />
									)}
								</View>
							) : (
								<View />
							)}
						</>
					)}
				</ScrollView>
				<View style={{ flexDirection: 'row', zIndex: -1, gap: 16 }}>
					<ButtonV2 preset='primary' 
							onPress={() => locationId
								? updateLocation(onNext) 
								: createNewLocation(onNext)}
							disabled={disableSave || (!canEdit && !canEditAssociation)}
							overrideStyle={{ marginLeft: 8 }}>
						<I18n color='white' weight='400' size={14}>general.save</I18n>
					</ButtonV2>

					<ButtonV2 preset='outline' onPress={onNext}>
						<I18n color='titlePrimary' weight='400' size={14}>general.cancel</I18n>
					</ButtonV2>
					
				</View>
			</View>
		</FormProvider>
	);
};

const styles = StyleSheet.create({
	container: {
		backgroundColor: colors.white,
		borderRadius: 20,
		paddingVertical: 24,
		paddingHorizontal: 24,
		minWidth: 875,
	},
	separator: {
		backgroundColor: colors.primary,
		height: 2,
		width: '100%',
		marginBottom: 5,
	},
	sectionContainer: {
		//...getShadowStyle(10, 'rgb(59, 112, 164)'),
		//padding: 20,
		//borderRadius: 8,
	},
	formRow: {
		//marginLeft: 40,
		zIndex: 'unset',
		height: 100,
	},
	tabsContainer: {
		flexDirection: 'row-reverse',
		borderBottomWidth: 1,
		borderBottomColor: '#e7e7e8',
	},
	tabContainer: {
		padding: 20,
		marginBottom: 20,
		borderRadius: 8,
		borderTopRightRadius: 0,
		zIndex: 'unset',
		flexDirection: 'row-reverse',
		justifyContent: 'space-between',
	},
	field: {
		width: 150,
		paddingBottom: 15,
	},
	dataContainer: {
		width: '50%',
	},
	cancelButton: {
		marginHorizontal: 10,
		backgroundColor: colors.white,
		borderColor: colors.lightPrimary,
		borderWidth: 1,
	},
	buttonTab: {
		width: 150,
		borderBottomWidth: 6,
		alignItems: 'center',
		padding: 3,
		marginTop: 20,
	},
	closeButton: {
		marginLeft: 8,
	},
	closeButtonText: {
		fontSize: 28,
		color: colors.darkGrey,
		lineHeight: 28,
	},
});

export default LocationSettingsModal;
